import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ComponentsTab from './ComponentsTab.tsx';

const queryClient = new QueryClient();

/**
 * Reads `component_type_id` query param(s) from the current URL and returns
 * them as an array of numbers.  Supports both `?component_type_id=1` (single)
 * and `?component_type_ids[]=1&component_type_ids[]=2` (multi) for forward
 * compatibility, but the Rails breakdown links currently emit single-value.
 */
function readInitialTypeIdsFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const ids = [];
  const parseId = (raw) => {
    if (!/^\d+$/.test(raw)) return null;
    return Number(raw);
  };

  const single = params.get('component_type_id');
  if (single) {
    const parsed = parseId(single);
    if (parsed !== null) ids.push(parsed);
  }

  params.getAll('component_type_ids[]').forEach((v) => {
    const parsed = parseId(v);
    if (parsed !== null) ids.push(parsed);
  });

  return [...new Set(ids)];
}

/**
 * Reads the `add_component=true` flag emitted by the building tab "+ Add"
 * menu. When present the Components tab auto-opens the Add Component drawer on
 * load; plain breakdown "view count" links share `?component_type_id=` but omit
 * this flag, so they land on the tab without opening the drawer.
 */
function readOpenAddOnLoadFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return params.get('add_component') === 'true';
}

function stripAddComponentParamFromUrl() {
  const url = new URL(window.location.href);
  url.searchParams.delete('add_component');
  window.history.replaceState({}, '', url);
}

export default function ComponentsWrapper({
  buildingId,
  attachableId,
  attachableType,
  supportsSubcomponents = false,
  hasComponentsV2 = false,
  canAccessSettings = false,
  canManageComponents = false
}) {
  const initialTypeIds = readInitialTypeIdsFromUrl();
  const openAddOnLoad = readOpenAddOnLoadFromUrl();

  useEffect(() => {
    if (openAddOnLoad) stripAddComponentParamFromUrl();
  }, [openAddOnLoad]);

  return (
    <QueryClientProvider client={queryClient}>
      <ComponentsTab
        buildingId={buildingId}
        attachableId={attachableId}
        attachableType={attachableType}
        supportsSubcomponents={supportsSubcomponents}
        hasComponentsV2={hasComponentsV2}
        canAccessSettings={canAccessSettings}
        canManageComponents={canManageComponents}
        initialTypeIds={initialTypeIds}
        openAddOnLoad={openAddOnLoad}
      />
    </QueryClientProvider>
  );
}

ComponentsWrapper.propTypes = {
  buildingId: PropTypes.number,
  attachableId: PropTypes.number,
  attachableType: PropTypes.string,
  supportsSubcomponents: PropTypes.bool,
  hasComponentsV2: PropTypes.bool,
  canAccessSettings: PropTypes.bool,
  canManageComponents: PropTypes.bool
};

ComponentsWrapper.defaultProps = {
  buildingId: null,
  attachableId: null,
  attachableType: null,
  supportsSubcomponents: false,
  hasComponentsV2: false,
  canAccessSettings: false,
  canManageComponents: false
};
